import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import AccessibilityButtons from '../common/AccessibilityButtons';
import './Navbar.css';

export default function Navbar() {
    const { user, userProfile, logout } = useAuth();
    const { plan } = usePermissions();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    if (!user) return null;

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const getPlanBadgeColor = () => {
        switch (plan) {
            case 'premium':
                return '#764ba2';
            case 'profissional':
                return '#667eea';
            default:
                return '#48bb78';
        }
    };

    return (
        <nav className="mg-navbar mg-animate-fade-in">
            <div className="mg-navbar-container">
                {/* Logo e Brand */}
                <div className="mg-navbar-brand">
                    <div className="mg-logo">
                        <img src="/images/logo.svg" alt="MicroGestor Logo" width="32" height="32" />
                    </div>
                    <div className="mg-brand-text">
                        <span className="mg-brand-name">MicroGestor</span>
                    </div>
                </div>

                {/* Hamburger Button for Mobile */}
                <button className="mg-hamburger-btn" onClick={toggleMobileMenu} aria-label="Menu">
                    <i className={`bi ${mobileMenuOpen ? 'bi-x' : 'bi-list'}`}></i>
                </button>

                {/* Navigation Links */}
                <div className={`mg-navbar-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                    {/* Mobile-only user section */}
                    <div className="mg-mobile-user-section">
                        <div className="mg-mobile-accessibility">
                            <AccessibilityButtons variant="navbar" />
                        </div>

                        <NavLink to="/perfil" className="mg-mobile-profile-link" onClick={closeMobileMenu}>
                            <div className="mg-user-avatar">
                                {user.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                                <div className="mg-mobile-username">{user.displayName || user.email?.split('@')[0]}</div>
                                <div className="mg-mobile-useremail">{user.email}</div>
                            </div>
                        </NavLink>

                        <NavLink to="/assinatura" className="mg-mobile-plan-link" onClick={closeMobileMenu} style={{ borderLeft: `3px solid ${getPlanBadgeColor()}` }}>
                            <i className="bi bi-star-fill" style={{ color: getPlanBadgeColor() }}></i>
                            <span>{plan === 'basico' ? 'Plano Básico' : plan === 'profissional' ? 'Plano Profissional' : 'Plano Premium'}</span>
                        </NavLink>

                        <div className="mg-mobile-divider"></div>
                    </div>

                    <NavLink to="/dashboard" className={({ isActive }) => `mg-nav-link ${isActive ? 'active' : ''}`} onClick={closeMobileMenu} data-tour="dashboard">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                        <span>Dashboard</span>
                    </NavLink>

                    <NavLink to="/produtos" className={({ isActive }) => `mg-nav-link ${isActive ? 'active' : ''}`} onClick={closeMobileMenu} data-tour="products">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                        </svg>
                        <span>Produtos</span>
                    </NavLink>

                    <NavLink to="/pedidos" className={({ isActive }) => `mg-nav-link mg-nav-link-pedidos ${isActive ? 'active' : ''}`} onClick={closeMobileMenu} data-tour="orders">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                        </svg>
                        <span>Pedidos</span>
                    </NavLink>

                    <NavLink to="/despesas" className={({ isActive }) => `mg-nav-link ${isActive ? 'active' : ''}`} onClick={closeMobileMenu} data-tour="expenses">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <span>Despesas</span>
                    </NavLink>

                    <NavLink to="/clientes" className={({ isActive }) => `mg-nav-link ${isActive ? 'active' : ''}`} onClick={closeMobileMenu} data-tour="clients">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        </svg>
                        <span>Clientes</span>
                    </NavLink>

                    <NavLink to="/relatorios" className={({ isActive }) => `mg-nav-link mg-nav-link-relatorios ${isActive ? 'active' : ''}`} onClick={closeMobileMenu} data-tour="reports">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                        </svg>
                        <span>Relatórios</span>
                    </NavLink>

                    {/* Mobile-only logout button */}
                    <div className="mg-mobile-divider"></div>
                    <button className="mg-mobile-logout-btn" onClick={() => { logout(); closeMobileMenu(); }}>
                        <i className="bi bi-box-arrow-right"></i>
                        <span>Sair</span>
                    </button>
                </div>

                {/* User Menu */}
                <div className="mg-navbar-user">
                    <AccessibilityButtons variant="navbar" />

                    <NavLink to="/assinatura" className="mg-plan-badge" style={{ background: getPlanBadgeColor() }}>
                        <i className="bi bi-star-fill"></i>
                        <span className="mg-plan-text">{plan === 'basico' ? 'Básico' : plan === 'profissional' ? 'Pro' : 'Premium'}</span>
                    </NavLink>

                    <NavLink to="/perfil" className="mg-user-info" title="Meu Perfil">
                        <div className="mg-user-avatar">
                            {user.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <span className="mg-user-name">
                            {user.displayName || user.email?.split('@')[0]}
                        </span>
                    </NavLink>
                    <button className="mg-btn-logout" onClick={logout} title="Sair">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile Menu Backdrop */}
            {mobileMenuOpen && <div className="mg-mobile-backdrop" onClick={closeMobileMenu}></div>}
        </nav>
    );
}
